/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 📱 TABLE MANAGEMENT COMPONENT
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 
 * Für Club Admins/Organizers:
 * - Liste aller Tische eines Clubs
 * - Approve/Disable/Delete Tische
 * - Spiel zuweisen/entfernen
 * - Status-Übersicht (online/offline)
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getClubTableDevices,
  approveTableDevice,
  disableTableDevice,
  updateTableDevice,
  assignGameToTable,
  unassignGameFromTable,
  deleteTableDevice,
  isTableOnline,
} from '../services/tableDevices';
import { subscribeToTableDevices, unsubscribe } from '../services/realtime';
import { useAuth } from '../contexts/AuthContext';
import type { TableDeviceWithDetails } from '../types';
import './TableManagement.css';

interface TableManagementProps {
  clubId: number;
}

export const TableManagement: React.FC<TableManagementProps> = ({ clubId }) => {
  const { t } = useTranslation();
  const { player } = useAuth();

  const [tables, setTables] = useState<TableDeviceWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);

  // ============================================================================
  // LOAD TABLES
  // ============================================================================

  const loadTables = async () => {
    try {
      const data = await getClubTableDevices(clubId);
      console.log('🔍 Loaded tables:', data);
      data.forEach(table => {
        console.log(`📱 Table ${table.table_name}: current_game_id=${table.current_game_id}, current_game=`, table.current_game);
      });
      setTables(data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading tables:', err);
      setError(err.message || 'Failed to load tables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTables();
  }, [clubId]);

  // Realtime subscription for table device updates
  useEffect(() => {
    if (!clubId) return;

    console.log(`Setting up realtime subscription for club ${clubId} table devices`);
    
    const channel = subscribeToTableDevices(
      clubId,
      (payload) => {
        console.log('Realtime table device update received:', payload);
        // Reload tables when any table changes
        loadTables();
      }
    );

    // Cleanup subscription on unmount
    return () => {
      console.log(`Cleaning up realtime subscription for club ${clubId} table devices`);
      unsubscribe(channel);
    };
  }, [clubId]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleApprove = async (tableId: number) => {
    if (!player) return;
    setProcessingId(tableId);
    try {
      await approveTableDevice(tableId, player.id);
      await loadTables();
    } catch (err: any) {
      console.error('Error approving table:', err);
      alert('Approve failed: ' + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDisable = async (tableId: number) => {
    setProcessingId(tableId);
    try {
      await disableTableDevice(tableId);
      await loadTables();
    } catch (err: any) {
      console.error('Error disabling table:', err);
      alert('Disable failed: ' + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleEnable = async (tableId: number) => {
    setProcessingId(tableId);
    try {
      await updateTableDevice(tableId, { status: 'approved' });
      await loadTables();
    } catch (err: any) {
      console.error('Error enabling table:', err);
      alert('Enable failed: ' + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleUnassignGame = async (tableId: number) => {
    setProcessingId(tableId);
    try {
      await unassignGameFromTable(tableId);
      await loadTables();
    } catch (err: any) {
      console.error('Error unassigning game:', err);
      alert('Unassign failed: ' + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (tableId: number, tableName: string) => {
    if (!window.confirm(t('tableDevices.management.confirmDelete'))) {
      return;
    }

    setProcessingId(tableId);
    try {
      await deleteTableDevice(tableId);
      await loadTables();
    } catch (err: any) {
      console.error('Error deleting table:', err);
      alert('Delete failed: ' + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  // ============================================================================
  // RENDERING
  // ============================================================================

  if (loading) {
    return (
      <div className="table-management">
        <h3>{t('tableDevices.management.title')}</h3>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-management">
        <h3>{t('tableDevices.management.title')}</h3>
        <div className="error-message">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <div className="table-management">
        <h3>{t('tableDevices.management.title')}</h3>
        <p className="no-tables">{t('tableDevices.management.noTables')}</p>
        <div className="info-box">
          <p>
            ℹ️ {t('tableDevices.management.registrationInfo')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-management">
      <h3>{t('tableDevices.management.title')}</h3>

      <div className="tables-grid">
        {tables.map((table) => {
          const online = isTableOnline(table.last_seen);
          const isProcessing = processingId === table.id;

          return (
            <div key={table.id} className={`table-card status-${table.status}`}>
              {/* Header */}
              <div className="table-header">
                <div>
                  <h4>{table.table_name}</h4>
                  {table.location && (
                    <p className="table-location">📍 {table.location}</p>
                  )}
                </div>
                <div className="status-indicators">
                  <span className={`status-badge ${table.status}`}>
                    {t(`tableDevices.status.${table.status}`)}
                  </span>
                  <span className={`online-badge ${online ? 'online' : 'offline'}`}>
                    {online ? '🟢' : '🔴'}{' '}
                    {t(`tableDevices.status.${online ? 'online' : 'offline'}`)}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="table-info">
                <div className="info-row">
                  <span className="info-label">
                    {t('tableDevices.management.registeredAt')}:
                  </span>
                  <span className="info-value">
                    {new Date(table.registered_at).toLocaleDateString()}
                  </span>
                </div>

                {table.approved_by_player && (
                  <div className="info-row">
                    <span className="info-label">
                      {t('tableDevices.management.approvedBy')}:
                    </span>
                    <span className="info-value">
                      {table.approved_by_player.preName} {table.approved_by_player.name}
                    </span>
                  </div>
                )}

                {table.last_seen && (
                  <div className="info-row">
                    <span className="info-label">
                      {t('tableDevices.management.lastSeen')}:
                    </span>
                    <span className="info-value">
                      {new Date(table.last_seen).toLocaleTimeString()}
                    </span>
                  </div>
                )}

                {/* Current Game */}
                {table.current_game_id ? (
                  <div className="info-row game-info">
                    <span className="info-label">
                      {t('tableDevices.management.assignedGame')}:
                    </span>
                    <span className="info-value">
                      🎱 Game #{table.current_game_id}
                      {table.current_game && (
                        <> • {table.current_game.player_a?.preName || 'Player A'} vs{' '}
                        {table.current_game.player_b?.preName || 'Player B'}</>
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="info-row">
                    <span className="info-label">
                      {t('tableDevices.management.assignedGame')}:
                    </span>
                    <span className="info-value no-game">
                      {t('tableDevices.management.noGameAssigned')}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="table-actions">
                {table.status === 'pending' && (
                  <button
                    onClick={() => handleApprove(table.id)}
                    disabled={isProcessing}
                    className="btn-approve"
                  >
                    ✅ {t('tableDevices.management.approve')}
                  </button>
                )}

                {table.status === 'approved' && (
                  <button
                    onClick={() => handleDisable(table.id)}
                    disabled={isProcessing}
                    className="btn-disable"
                  >
                    🚫 {t('tableDevices.management.disable')}
                  </button>
                )}

                {table.status === 'disabled' && (
                  <button
                    onClick={() => handleEnable(table.id)}
                    disabled={isProcessing}
                    className="btn-enable"
                  >
                    ✅ {t('tableDevices.management.enable')}
                  </button>
                )}

                {table.current_game_id && (
                  <button
                    onClick={() => handleUnassignGame(table.id)}
                    disabled={isProcessing}
                    className="btn-unassign"
                  >
                    ❌ {t('tableDevices.management.unassignGame')}
                  </button>
                )}

                <button
                  onClick={() => handleDelete(table.id, table.table_name)}
                  disabled={isProcessing}
                  className="btn-delete"
                >
                  🗑️ {t('tableDevices.management.deleteTisch')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

